import { clsx } from 'clsx'
import { type ComponentProps, type FC } from 'react'

type HeadingOwnProps = {
  /**
   * Sets heading level, h1 through h6
   */
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- possible heading element levels from https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Heading_Elements
  level: 1 | 2 | 3 | 4 | 5 | 6

  /**
   * The size of the component. The size is responsive, meaning it adjusts for `mobile` and `desktop` screens.
   *
   * Note: The heading font size on mobile has a limit, so `s` and `m` appear the same on `mobile` screens.
   */
  // eslint-disable-next-line perfectionist/sort-union-types -- keep semantic order
  size: 's' | 'm' | 'l' | 'xl' | '2xl'
}

type HeadingProps = ComponentProps<`h${HeadingOwnProps['level']}`> &
  HeadingOwnProps

/**
 * Heading is used to render semantic HTML heading elements
 */
export const Heading: FC<HeadingProps> = (props) => {
  const { className, level, size, ...rest } = props

  const Component = `h${level}` as const

  return (
    <Component
      className={clsx(
        'heading',
        size === 's' && 'heading--size-s',
        size === 'm' && 'heading--size-m',
        size === 'l' && 'heading--size-l',
        size === 'xl' && 'heading--size-xl',
        size === '2xl' && 'heading--size-2xl',
        className,
      )}
      {...rest}
    />
  )
}
